import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff, User } from 'lucide-react';
import { auth, RegisterRequest, AuthResponse } from '../api/authApi';
import { GoogleLoginButton } from './GoogleLoginButton';
import { AccountLinkingModal } from './AccountLinkingModal';
import { Loader } from '@mantine/core';
import './Auth.css';
import LogoSvg from '../assets/logo.svg';

interface RegisterFormProps {
  onRegister: (user: AuthResponse['user'], token: string) => void;
  onSwitchToLogin: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onRegister, onSwitchToLogin }) => {
  const [formData, setFormData] = useState<RegisterRequest>({
    name: '',
    email: '',
    password: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Google login and account linking state
  const [showLinkingModal, setShowLinkingModal] = useState(false);
  const [linkingData, setLinkingData] = useState<{
    userId: number;
    googleToken: string;
    userEmail?: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await auth.register(formData);
      
      // Store token and user data as per server specification
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('currentUser', JSON.stringify(data.user)); // Keep for compatibility
      
      // Call parent callback
      onRegister(data.user, data.token);
    } catch (error: unknown) {
      console.error('Registration error:', error);
      setError(error instanceof Error ? error.message : 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = (user: any, token: string) => {
    onRegister(user, token);
  };

  const handleGoogleError = (error: string) => {
    setError(error);
  };

  const handleAccountLinkingRequired = (userId: number, googleToken: string) => {
    setLinkingData({ userId, googleToken });
    setShowLinkingModal(true);
  };

  const handleLinkingSuccess = (user: any, token: string) => {
    setShowLinkingModal(false);
    setLinkingData(null);
    onRegister(user, token);
  };

  const handleLinkingClose = () => {
    setShowLinkingModal(false);
    setLinkingData(null);
  };

  return (
    <div className="auth-container">
      <header className="auth-header-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={LogoSvg} alt="SymphonyTodo Logo" style={{ height: 40, width: 40, display: 'block', marginRight: 10 }} />
          <h1 style={{ fontWeight: 700, fontSize: 28, letterSpacing: '-1px', margin: 0, textAlign: 'center', lineHeight: '40px' }}>SymphonyTodo</h1>
        </div>
      </header>
      <main className="auth-content-section">
        <div className="auth-flex-layout">
          <div className="auth-info-panel">
            <div style={{ fontSize: 18, marginBottom: 8, fontWeight: 600, color: '#15803d' }}>
              🎉 Welcome to <span style={{ color: '#2563eb', fontWeight: 600 }}>SymphonyTodo</span>
            </div>
            <div style={{ fontSize: 15, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
              Sign up to organize your work and life with your own powerful task management platform. Get started today and experience the difference organized productivity can make.
            </div>
            <div style={{ fontSize: 17, marginBottom: 8, fontWeight: 600, color: '#b45309' }}>
              ✨ What you get for free
            </div>
            <div style={{ fontSize: 15, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
              • <strong>Dashboard</strong> - Overview of all your tasks and projects<br/>
              • <strong>Notes</strong> - Rich text editor and post it notes for capturing ideas and documentation<br/>
              • <strong>Todo Lists</strong> - Create, organize, and track your tasks with ease
            </div>
            <div style={{ fontSize: 17, marginBottom: 8, fontWeight: 600, color: '#7c3aed' }}>
              🚀 Upgrade to Premium
            </div>
            <div style={{ fontSize: 15, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
              Unlock <strong>Kanban boards</strong> for visual project management and <strong>Calendar view</strong> to see your tasks in a weekly or monthly view. Perfect for teams and power users.
            </div>
            <div style={{ fontSize: 17, marginBottom: 8, fontWeight: 600, color: '#15803d' }}>
              🔒 Your data is secure
            </div>
            <div style={{ fontSize: 15, color: '#555', lineHeight: 1.6 }}>
              Your password is protected using BCrypt encryption - we never store your actual password. Forgot your password? No problem, we have a secure reset feature to get you back in quickly.
            </div>
            <div style={{ fontSize: 14, color: '#888', marginTop: 24, textAlign: 'left' }}>
              Questions or feedback? Reach out to Ola at <a href="mailto:contact@symphonytodo.com" style={{ color: '#2563eb', textDecoration: 'underline' }}>contact@symphonytodo.com</a> - we'd love to hear from you!
            </div>
          </div>
          <div className="auth-card">
            <div className="auth-header">
              <h1>Create Account</h1>
              <p>Join SymphonyTodo to organize your tasks</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <div className="input-wrapper">
                  <User size={18} className="input-icon" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    autoComplete="name"
                    autoCorrect="off"
                    spellCheck="false"
                    tabIndex={1}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-wrapper">
                  <Mail size={18} className="input-icon" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                    autoComplete="email"
                    autoCorrect="off"
                    spellCheck="false"
                    tabIndex={2}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <Lock size={18} className="input-icon" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password (min 6 characters)"
                    required
                    autoComplete="new-password"
                    autoCorrect="off"
                    spellCheck="false"
                    minLength={6}
                    tabIndex={3}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="input-wrapper">
                  <Lock size={18} className="input-icon" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    required
                    autoComplete="new-password"
                    autoCorrect="off"
                    spellCheck="false"
                    tabIndex={4}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    tabIndex={-1}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={isLoading || !formData.name || !formData.email || !formData.password || !confirmPassword}
              >
                {isLoading ? <Loader type="bars" size="sm" color="white" /> : 'Create Account'}
              </button>
              
              <div className="auth-divider" style={{ margin: '24px 0', textAlign: 'center', position: 'relative' }}>
                <span style={{ 
                  backgroundColor: '#fff', 
                  padding: '0 16px', 
                  color: '#6b7280', 
                  fontSize: '14px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  or
                </span>
                <div style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: 0, 
                  right: 0, 
                  height: '1px', 
                  backgroundColor: '#e5e7eb',
                  zIndex: 0
                }} />
              </div>

              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                onAccountLinkingRequired={handleAccountLinkingRequired}
                disabled={isLoading}
                text="Continue with Google"
              />
            </form>

            <div className="auth-footer">
              <p>
                Already have an account?{' '}
                <button
                  type="button"
                  className="auth-link"
                  onClick={onSwitchToLogin}
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </main>
      
      {/* Account Linking Modal */}
      {showLinkingModal && linkingData && (
        <AccountLinkingModal
          isOpen={showLinkingModal}
          onClose={handleLinkingClose}
          onSuccess={handleLinkingSuccess}
          userId={linkingData.userId}
          googleToken={linkingData.googleToken}
          userEmail={linkingData.userEmail}
        />
      )}
    </div>
  );
};
